import React, { createContext, useContext, useState } from 'react';
import clsx from 'clsx';
import styles from './ExpandableCard.module.scss';

interface ExpandableCardContextType {
    isExpanded: boolean;
    toggleExpanded: () => void;
}

const ExpandableCardContext = createContext<ExpandableCardContextType | undefined>(undefined);

export const useExpandableCard = () => {
    const context = useContext(ExpandableCardContext);
    if (!context) {
        throw new Error('ExpandableCard components must be used within ExpandableCard');
    }
    return context;
};

type ExpandableCardProps = React.ComponentProps<'div'> & {
    defaultExpanded?: boolean;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
};

export const ExpandableCard: React.FC<ExpandableCardProps> = ({
    children,
    className,
    defaultExpanded = false,
    expanded,
    onExpandedChange,
    ...props
}) => {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

    const isControlled = expanded !== undefined;
    const isExpanded = isControlled ? expanded : internalExpanded;

    const toggleExpanded = () => {
        const newValue = !isExpanded;
        if (!isControlled) {
            setInternalExpanded(newValue);
        }
        onExpandedChange?.(newValue);
    };

    return (
        <ExpandableCardContext.Provider value={{ isExpanded, toggleExpanded }}>
            <div
                className={clsx(styles.card, className)}
                {...props}
            >
                {children}
            </div>
        </ExpandableCardContext.Provider>
    );
};
